// Hợp đồng của deal-service (đặc tả docs/backend/services/deal-service.md, saga docs/backend/sagas.md SG-1…SG-6):
// phiếu cọc → hợp đồng, duyệt/từ chối, thanh toán, phí CĐT, đổi căn, huỷ; điều phối saga với booking.
// Quy ước: tiền VND số nguyên (number, ≤ 9e15); phần trăm number; thời điểm ISO 8601 UTC; tháng YYYY-MM (VN); id UUID.
// Tệp chứng từ tải qua gateway POST /api/files (purpose deal-proof) — lệnh chỉ mang fileId + tên (IMPLEMENTATION §4b).
import { z } from 'zod';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const msg = (message: string) => ({ errorMap: () => ({ message }) });
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const reqText = (max: number, message: string) => str(message).trim().min(1, message).max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const uuidReq = (required: string) => str(required).uuid('Mã định danh không hợp lệ');
const num = (required = REQUIRED) => z.number({ required_error: required, invalid_type_error: 'Phải là số' });
const money = num('Nhập số tiền').int('Số tiền phải là số nguyên').min(0, 'Số tiền không được âm').max(9e15, 'Số tiền vượt giới hạn');
const bool = z.boolean({ required_error: REQUIRED, invalid_type_error: 'Phải là đúng/sai' });
const decimals = (d: number) => (x: number) => Math.abs(x * 10 ** d - Math.round(x * 10 ** d)) < 1e-6;
const isoTime = str().datetime({ offset: true, message: 'Thời điểm phải theo ISO 8601' });
const monthKey = str().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Tháng dạng YYYY-MM');
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};
const page = qInt(1, 100_000, 1);

// ---------- Hằng số dùng chung FE/BE (nhãn trạng thái ở packages/domain DEAL_STATUS) ----------
export const DEAL_STATUSES = ['pending', 'deposited', 'contracted', 'completed', 'rejected', 'cancelled'] as const;
export type DealStatus = (typeof DEAL_STATUSES)[number];
/** Giao dịch "sống" (tính doanh số, hoa hồng). */
export const LIVE_DEAL_STATUSES = ['deposited', 'contracted', 'completed'] as const;
export const DEAL_LIFECYCLES = ['submitting', 'active', 'aborted'] as const;
export type DealLifecycle = (typeof DEAL_LIFECYCLES)[number];
export const UNIT_LINK_STATES = ['reserving', 'linked', 'changing', 'released'] as const;
export type UnitLinkState = (typeof UNIT_LINK_STATES)[number];
export const UNIT_SYNC_STATES = ['in_sync', 'awaiting', 'stuck'] as const;
export type UnitSyncState = (typeof UNIT_SYNC_STATES)[number];
export const EXPECTED_UNIT_STATUSES = ['pending', 'deposited', 'contracted', 'open'] as const;
export type ExpectedUnitStatus = (typeof EXPECTED_UNIT_STATUSES)[number];
/** "Hình thức" của phiếu chốt căn (BookingForm). */
export const DEPOSIT_METHODS = ['Chuyển khoản', 'Tiền mặt tại sàn', 'Chuyển khoản trực tiếp CĐT', 'Quẹt thẻ'] as const;
/** Hình thức thanh toán đợt (= packages/domain PAY_METHODS). */
export const PAY_METHODS = ['Chuyển khoản', 'Tiền mặt tại CĐT', 'Vay ngân hàng giải ngân', 'Khác'] as const;
/** Danh sách "Lý do huỷ" (modal huỷ); server nhận chuỗi tự do để giữ dữ liệu cũ. */
export const CANCEL_REASONS = [
  'Khách không thu xếp được tài chính',
  'Khách đổi ý trong thời hạn cho phép',
  'Không được ngân hàng duyệt vay',
  'CĐT không duyệt hồ sơ',
  'Khách vi phạm tiến độ thanh toán',
  'Khác',
] as const;
export const DISCOUNT_PCT_MAX = 20;
export const DEAL_TABS = ['pending', 'active', 'contracted', 'due', 'completed', 'closed', 'all'] as const;
export type DealTab = (typeof DEAL_TABS)[number];
export const SAGA_TYPES = ['deal_submission', 'unit_change'] as const;
export type SagaType = (typeof SAGA_TYPES)[number];
export const SAGA_STATES = ['running', 'compensating', 'completed', 'failed', 'stuck'] as const;
export type SagaState = (typeof SAGA_STATES)[number];
export const SUBMISSION_STATES = ['reserving', 'compensating', 'submitted', 'failed', 'stuck'] as const;
export type SubmissionState = (typeof SUBMISSION_STATES)[number];
/** Lý do gửi phiếu thất bại: từ booking (booking.RESERVATION_REJECT_REASONS) hoặc quá hạn bù trừ. */
export const SUBMISSION_FAIL_REASONS = ['UNIT_NOT_LOCKED', 'UNIT_HELD_BY_OTHER', 'LOCK_EXPIRED', 'UNIT_NOT_FOUND', 'CANCELLED', 'TIMEOUT'] as const;
export type SubmissionFailReason = (typeof SUBMISSION_FAIL_REASONS)[number];
export const UNIT_CHANGE_FAIL_REASONS = ['TARGET_UNIT_NOT_AVAILABLE', 'DEAL_UNIT_NOT_LINKED', 'UNIT_NOT_FOUND', 'CANCELLED'] as const;
export type UnitChangeFailReason = (typeof UNIT_CHANGE_FAIL_REASONS)[number];
export const TIMELINE_KINDS = ['submitted', 'confirmed', 'rejected', 'contracted', 'payment', 'devFee', 'cancelled', 'unitChanged'] as const;
export type TimelineKind = (typeof TIMELINE_KINDS)[number];
/** cancelled: đợt chưa thu đủ của giao dịch đã huỷ / bị từ chối — không còn là việc thu tiền (không "Quá hạn"). */
export const INSTALLMENT_ROW_STATUSES = ['paid', 'overdue', 'partial', 'upcoming', 'cancelled'] as const;
export type InstallmentRowStatus = (typeof INSTALLMENT_ROW_STATUSES)[number];
export const INSTALLMENT_ROW_STATUS_LABEL: Record<InstallmentRowStatus, string> = { paid: 'Đã thu', overdue: 'Quá hạn', partial: 'Thu một phần', upcoming: 'Chưa đến', cancelled: 'Đã huỷ' };

/** Khoá idempotency chuẩn của saga (sagas.md §0.3). */
export const sagaKeys = {
  reserve: (dealId: string) => `${dealId}:reserve`,
  reserveCancel: (dealId: string) => `${dealId}:reserve-cancel`,
  swap: (sagaId: string) => `${sagaId}:swap`,
  swapCancel: (sagaId: string) => `${sagaId}:swap-cancel`,
  sync: (dealId: string, dealVersion: number) => `${dealId}:sync:${dealVersion}`,
};

// ---------- Dữ liệu vào: lệnh ----------
/** Tệp đã tải lên gateway (POST /api/files, purpose = deal-proof). */
export const fileAttachment = z.object({
  fileId: uuidReq('Thiếu tệp'),
  name: reqText(200, 'Thiếu tên tệp'),
  contentType: text(100).optional(),
  size: num().int('Dung lượng không hợp lệ').min(0, 'Dung lượng không hợp lệ').max(10 * 1024 * 1024, 'Tệp tối đa 10 MB').optional(),
});
export type FileAttachment = z.infer<typeof fileAttachment>;
export const dealRef = z.object({ dealId: uuid });

/**
 * Gửi phiếu cọc (SG-1) — header Idempotency-Key bắt buộc. Thứ tự lỗi nghiệp vụ như BookingForm (bookingFormErrors):
 * khách (crm), cọc tối thiểu, chứng từ (422), giữ căn hợp lệ (booking).
 */
export const submitBookingInput = z.object({
  unitId: uuidReq('Chọn căn'),
  customerId: uuidReq('Chọn khách hàng'),
  /** CCCD trên phiếu — chỉ chữ số, ≤ 20. */
  idNo: z.preprocess(
    (v) => (typeof v === 'string' ? (v.trim() === '' ? null : v.replace(/\s/g, '')) : v),
    str().regex(/^\d{1,20}$/, 'Số CCCD chỉ gồm chữ số (tối đa 20)').nullish(),
  ),
  referrerId: uuid.nullish(),
  discountPct: num()
    .min(0, `Chiết khấu từ 0 đến ${DISCOUNT_PCT_MAX}%`)
    .max(DISCOUNT_PCT_MAX, `Chiết khấu từ 0 đến ${DISCOUNT_PCT_MAX}%`)
    .refine(decimals(2), 'Chiết khấu tối đa 2 chữ số thập phân')
    .default(0),
  discountAmt: money.default(0),
  deposit: money,
  payMethod: z.enum(DEPOSIT_METHODS, msg('Hình thức cọc không hợp lệ')).default('Chuyển khoản'),
  proofRef: z.preprocess((v) => (v == null ? '' : v), text(100)),
  proofFiles: z.array(fileAttachment).max(20, 'Tối đa 20 tệp chứng từ').default([]),
  proofLater: bool.default(false),
  note: z.preprocess((v) => (v == null ? '' : v), text(2000)),
  /** Giá FE đang hiển thị — lệch mirror ⇒ 409 LIST_PRICE_CHANGED. */
  expectedListPrice: money.optional(),
});
export const approveDepositInput = dealRef;
export const rejectDepositInput = z.object({ dealId: uuid, reason: reqText(500, 'Nhập lý do từ chối') });
export const signContractInput = z.object({ dealId: uuid, contractNo: reqText(100, 'Nhập số hợp đồng'), signedAt: isoTime.optional() });
/** Ghi nhận thanh toán — header Idempotency-Key bắt buộc. */
export const recordPaymentInput = z.object({
  dealId: uuid,
  amount: num('Nhập số tiền').int('Số tiền phải là số nguyên').positive('Nhập số tiền').max(9e15, 'Số tiền vượt giới hạn'),
  method: z.enum(PAY_METHODS, msg('Hình thức thanh toán không hợp lệ')).default('Chuyển khoản'),
  ref: z.preprocess((v) => (v == null ? '' : v), text(100)),
  paidAt: isoTime.optional(),
  files: z.array(fileAttachment).max(10, 'Tối đa 10 tệp mỗi lần thanh toán').default([]),
});
export const setDeveloperFeePaidInput = z.object({ dealId: uuid, paid: bool });
/** Đổi căn (SG-6) — header Idempotency-Key; trả 202 { sagaId }. */
export const changeDealUnitInput = z.object({ dealId: uuid, toUnitId: uuidReq('Chọn căn mới') });
/** Huỷ giao dịch (SG-5) — header Idempotency-Key; refund > đã thu chỉ cảnh báo (REFUND_EXCEEDS_PAID). */
export const cancelDealInput = z.object({
  dealId: uuid,
  reason: reqText(200, 'Chọn lý do huỷ'),
  refund: num('Số tiền hoàn không hợp lệ').int('Số tiền hoàn không hợp lệ').min(0, 'Số tiền hoàn không hợp lệ').max(9e15, 'Số tiền hoàn không hợp lệ').default(0),
});
export const retryUnitSyncInput = dealRef;
export const retrySagaInput = z.object({ sagaId: uuid });

// ---------- Truy vấn công khai ----------
export const listDealsInput = z.object({
  tab: z.enum(DEAL_TABS, msg('Tab không hợp lệ')).default('all'),
  projectId: uuid.optional(),
  /** Không dấu: mã GD, mã căn, khách, sale, số HĐ. */
  q: text(200).optional(),
  sort: z.enum(['createdAt', 'value', 'paid'], msg('Cách sắp xếp không hợp lệ')).default('createdAt'),
  dir: z.enum(['asc', 'desc'], msg('Chiều sắp xếp không hợp lệ')).default('desc'),
  page,
  pageSize: qInt(1, 200, 50),
});
export const getDealsOverviewInput = z.object({ month: monthKey.optional() });
export const getDealInput = dealRef;
export const getDealSubmissionInput = dealRef;
export const getUnitChangeInput = z.object({ sagaId: uuid });
export const getDealAnnouncementInput = dealRef;
export const searchDealsInput = z.object({ q: reqText(200, 'Nhập từ khoá'), limit: qInt(1, 20, 6) });
export const getDealAlertSummaryInput = z.object({});
export const getDealPipelineCountsInput = z.object({});

// ---------- Truy vấn nội bộ ----------
export const listDealsByCustomerInput = z.object({ customerId: uuid });
export const getDealTotalsByProjectInput = z.object({ projectIds: z.array(uuid).min(1, 'Chọn ít nhất một dự án').max(200, 'Tối đa 200 dự án') });
export const getDealStatesInput = z.object({
  dealIds: z.array(uuid).max(1000, 'Tối đa 1.000 giao dịch').optional(),
  updatedSince: isoTime.optional(),
  afterId: uuid.optional(),
  limit: qInt(1, 1000, 500),
});
export const getDealUnitLinksInput = z.object({ unitIds: z.array(uuid).max(1000, 'Tối đa 1.000 căn').optional(), afterDealId: uuid.optional(), limit: qInt(1, 1000, 500) });
/** Gateway GET /api/files/:id (purpose deal-proof / deal-contract, người gọi không phải người tải lên): tệp thuộc giao dịch trong phạm vi xem. */
export const canViewDealFileInput = z.object({ fileId: uuid });

/** Tên RPC: deal.cmd.<Name> / deal.qry.<Name>. */
export const RPC = {
  cmd: [
    'SubmitBooking',
    'ApproveDeposit',
    'RejectDeposit',
    'SignContract',
    'RecordPayment',
    'SetDeveloperFeePaid',
    'ChangeDealUnit',
    'CancelDeal',
    'RetryUnitSync',
    'RetrySaga',
  ],
  qry: [
    'ListDeals',
    'GetDealsOverview',
    'GetDeal',
    'GetDealSubmission',
    'GetUnitChange',
    'GetDealAnnouncement',
    'SearchDeals',
    'GetDealAlertSummary',
    'GetDealPipelineCounts',
    // nội bộ
    'ListDealsByCustomer',
    'GetDealTotalsByProject',
    'GetDealStates',
    'GetDealUnitLinks',
    'CanViewDealFile',
  ],
} as const;

// ---------- DTO / read model ----------
export interface NextInstallment {
  seq: number;
  label: string;
  due: string;
  /** Số còn thiếu để đủ luỹ kế tới hết đợt này. */
  remaining: number;
}
/** Trạng thái giao dịch đi kèm mọi sự kiện deal.deposit.* / contract / payment / deal.* (consumer áp version mới hơn). */
export interface DealState {
  dealId: string;
  code: string;
  status: DealStatus;
  projectId: string;
  unitId: string;
  customerId: string;
  staffId: string;
  referrerId: string | null;
  listPrice: number;
  discountPct: number;
  discountAmt: number;
  value: number;
  deposit: number;
  paymentsTotal: number;
  /** D16: cọc (khi đã xác nhận) + Σ thanh toán. */
  paidTotal: number;
  createdAt: string;
  confirmedAt: string | null;
  contractedAt: string | null;
  completedAt: string | null;
  rejectedAt: string | null;
  cancelledAt: string | null;
  devPaidAt: string | null;
  contractNo: string | null;
  /** Đợt kế tiếp chưa thu đủ (nextInstallment) — chỉ với deposited/contracted; còn lại null. */
  nextDue: NextInstallment | null;
  version: number;
}
/** Tham chiếu hiển thị cho nhật ký/thông báo. */
export interface DealRefs {
  unitCode: string;
  projectName: string;
}
export interface DealRow {
  dealId: string;
  code: string;
  createdAt: string;
  unitId: string;
  unitCode: string;
  projectId: string;
  projectName: string;
  customerId: string;
  customerName: string;
  customerPhoneMasked: string;
  staffId: string;
  staffName: string;
  referrerId: string | null;
  value: number;
  paidRatio: number;
  next: { due: string; remaining: number } | null;
  status: DealStatus;
  /** Chỉ với pending: số phút chờ duyệt. */
  pendingMinutes: number | null;
}
export type DealTabCounts = Record<DealTab, number>;
export interface DealList {
  items: DealRow[];
  counts: DealTabCounts;
  total: number;
  /** Không lọc tab/q (chọn câu trống). */
  totalAll: number;
  page: number;
  pageSize: number;
  serverTime: string;
}
export interface DealsOverview {
  month: string;
  gtgd: number;
  liveCount: number;
  pendingCount: number;
  approveSlaMinutes: number;
  overdueInstallments: number;
  dueWithin7Days: number;
  feeOpen: number;
  canApprove: boolean;
}
export interface DealFileView {
  fileId: string;
  name: string;
  contentType: string | null;
  /** /api/files/:id */
  url: string;
}
export interface ScheduleRow {
  seq: number;
  label: string;
  pct: number;
  amount: number;
  due: string;
  status: InstallmentRowStatus;
}
export interface DealPaymentView {
  paymentId: string;
  paidAt: string;
  amount: number;
  method: string;
  ref: string;
  files: DealFileView[];
  recordedBy: string;
  recordedByName: string | null;
  recordedAt: string;
}
export interface TimelineEntryView {
  seq: number;
  at: string;
  byStaffId: string | null;
  byName: string | null;
  kind: TimelineKind;
  action: string;
  note: string | null;
}
export interface DealUnitChangeView {
  at: string;
  by: string;
  byName: string | null;
  fromUnitId: string;
  toUnitId: string;
  fromCode: string;
  toCode: string;
  fromListPrice: number;
  toListPrice: number;
  fromValue: number;
  toValue: number;
  sagaId: string;
}
export interface DealActions {
  canApprove: boolean;
  canReject: boolean;
  canSign: boolean;
  canRecordPayment: boolean;
  canChangeUnit: boolean;
  canCancel: boolean;
  canToggleDevFee: boolean;
  canCopyAnnouncement: boolean;
}
export interface DealDetail {
  dealId: string;
  code: string;
  status: DealStatus;
  projectId: string;
  unitId: string;
  customerId: string;
  staffId: string;
  referrerId: string | null;
  listPrice: number;
  discountPct: number;
  discountAmt: number;
  value: number;
  deposit: number;
  payMethod: string;
  proofRef: string;
  proofLater: boolean;
  proofFiles: DealFileView[];
  note: string;
  /** Chỉ người có deals.approve / deals.manage hoặc người chốt (D28). */
  customerIdNo: string | null;
  paymentsTotal: number;
  paidTotal: number;
  paidRatio: number;
  remaining: number;
  fee: number;
  agencyRate: number;
  createdAt: string;
  createdBy: string;
  confirmedAt: string | null;
  confirmedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  rejectReason: string | null;
  contractNo: string | null;
  contractedAt: string | null;
  contractedBy: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancelReason: string | null;
  refund: number | null;
  devPaidAt: string | null;
  devPaidBy: string | null;
  pending: { waitMinutes: number; approveSlaMinutes: number; late: boolean } | null;
  /** Rỗng khi chưa xác nhận cọc (S4); dòng "Đặt cọc" FE dựng từ deposit + confirmedAt. */
  schedule: ScheduleRow[];
  next: NextInstallment | null;
  payments: DealPaymentView[];
  timeline: TimelineEntryView[];
  unitChanges: DealUnitChangeView[];
  refs: {
    unitCode: string;
    unitType: string | null;
    unitArea: number | null;
    projectName: string;
    customerName: string;
    customerPhoneMasked: string;
    /**
     * Chủ khách (crm) — FE áp canSeePhone (C17) để hiện nút "Hiện số" (crm RevealCustomerPhone, context deal_view). Gateway
     * GET /deals/:id gộp bản mới nhất từ crm GetCustomerSummaries (§4.3); thiếu ⇒ bản mirror của deal.
     */
    customerOwnerId?: string | null;
    staffName: string;
    staffTeamName: string | null;
    referrerName: string | null;
  };
  unitLinkState: UnitLinkState;
  unitSync: { state: UnitSyncState; expected: ExpectedUnitStatus | null; since: string | null };
  activeSagaId: string | null;
  actions: DealActions;
  serverTime: string;
  version: number;
}
export interface SubmitBookingResult {
  dealId: string;
  code: string;
  submission: { state: SubmissionState };
}
export interface DealSubmissionView {
  state: SubmissionState;
  reasonCode: SubmissionFailReason | null;
  message: string | null;
  deal: { dealId: string; code: string; unitCode: string; status: DealStatus } | null;
  /** Saga deal_submission mới nhất của giao dịch (POST /deals/sagas/:sagaId/retry); null khi chưa có. */
  sagaId: string | null;
  /** state = stuck ∧ người xem có deals.manage ⇒ "Thử lại". */
  canRetry: boolean;
}
export interface UnitChangeView {
  sagaId: string;
  state: SagaState;
  reasonCode: UnitChangeFailReason | null;
  message: string | null;
  toUnitId: string;
  toUnitCode: string | null;
}
export interface ChangeDealUnitResult {
  sagaId: string;
}
export interface CancelDealResult {
  /** VD ['REFUND_EXCEEDS_PAID']. */
  warnings: string[];
}
export interface DealAnnouncement {
  text: string;
}
export interface DealSearchHit {
  dealId: string;
  code: string;
  unitCode: string;
  customerName: string;
  status: DealStatus;
  value: number;
}
export interface DealAlertSummary {
  /** Chỉ khi có deals.approve (mọi phiếu chờ của tenant). */
  pending?: { count: number; oldestCreatedAt: string | null; late: boolean };
  /** Chỉ khi có deals.manage hoặc booking.lock (trong phạm vi deals.view + người giới thiệu). */
  dueSoon?: { count: number };
}
export interface DealPipelineCounts {
  pendingCount: number;
}
// Nội bộ
export interface CustomerDealItem {
  dealId: string;
  code: string;
  unitCode: string;
  projectName: string;
  value: number;
  status: DealStatus;
  createdAt: string;
}
export interface DealTotalsByProject {
  projectId: string;
  liveValue: number;
  /** Mọi trạng thái (gồm từ chối/huỷ) — "n phiếu". */
  dealCount: number;
}
/** deal.qry.CanViewDealFile: tệp gắn (mọi trạng thái) với giao dịch người gọi xem được (phạm vi deals.view hoặc người giới thiệu). */
export interface DealFileAccess {
  allowed: boolean;
}
export interface DealUnitLink {
  dealId: string;
  unitId: string;
  status: DealStatus;
  unitLinkState: UnitLinkState;
  expectedUnitStatus: ExpectedUnitStatus;
  version: number;
}

// ---------- Sự kiện ----------
export const EVENTS = {
  // SG-1 Gửi phiếu cọc
  submissionUnitReservationRequested: 'deal.submission.unitReservationRequested',
  submissionUnitReservationCancelRequested: 'deal.submission.unitReservationCancelRequested',
  submissionFailed: 'deal.submission.failed',
  submissionStuck: 'deal.submission.stuck',
  depositSubmitted: 'deal.deposit.submitted',
  // SG-2 / SG-3 duyệt, từ chối
  approvalSlaBreached: 'deal.approval.slaBreached',
  depositConfirmed: 'deal.deposit.confirmed',
  depositRejected: 'deal.deposit.rejected',
  // SG-4 ký HĐ, thanh toán, hoàn tất
  contractSigned: 'deal.contract.signed',
  paymentRecorded: 'deal.payment.recorded',
  dealCompleted: 'deal.deal.completed',
  developerFeeUpdated: 'deal.developerFee.updated',
  // SG-5 huỷ
  dealCancelled: 'deal.deal.cancelled',
  // SG-6 đổi căn
  unitChangeUnitSwapRequested: 'deal.unitChange.unitSwapRequested',
  unitChangeCancelRequested: 'deal.unitChange.cancelRequested',
  unitChanged: 'deal.unit.changed',
  unitChangeFailed: 'deal.unitChange.failed',
  unitChangeStuck: 'deal.unitChange.stuck',
  // Đồng bộ căn sau pivot (sagas.md §8)
  unitSyncRequested: 'deal.unitSync.requested',
  unitSyncStuck: 'deal.unitSync.stuck',
  // Nhắc đợt thanh toán
  installmentDueSoon: 'deal.installment.dueSoon',
  installmentOverdue: 'deal.installment.overdue',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

/** Yêu cầu booking giữ chỗ căn cho giao dịch (SG-1 T1). aggregateId = dealId; correlationId = sagaId. */
export interface SubmissionUnitReservationRequested {
  sagaId: string;
  idempotencyKey: string; // '<dealId>:reserve'
  attempt: number;
  dealId: string;
  dealVersion: number;
  unitId: string;
  projectId: string;
  holderStaffId: string;
  customerId: string;
  requestedAt: string;
}
/** Bù trừ C1: huỷ yêu cầu giữ chỗ (booking chưa xử lý ⇒ ghi huỷ; đã giữ chỗ ⇒ khôi phục căn). */
export interface SubmissionUnitReservationCancelRequested {
  sagaId: string;
  idempotencyKey: string; // '<dealId>:reserve'
  cancelKey: string; // '<dealId>:reserve-cancel'
  attempt: number;
  dealId: string;
  dealVersion: number;
  unitId: string;
}
export interface SubmissionFailed {
  sagaId: string;
  dealId: string;
  code: string;
  unitId: string;
  unitCode: string;
  staffId: string;
  reasonCode: SubmissionFailReason;
  message: string;
}
export interface SubmissionStuck {
  sagaId: string;
  dealId: string;
  code: string;
  unitId: string;
  unitCode: string;
  step: 'reserve' | 'reserve_cancel';
  attempt: number;
}
/** Pivot SG-1: giao dịch hoạt động, chờ duyệt. idNo (PII) chỉ để crm lưu CCCD — audit loại bỏ. */
export interface DepositSubmitted {
  deal: DealState;
  refs: DealRefs;
  sagaId: string;
  payMethod: string;
  proofRef: string;
  proofLater: boolean;
  proofFileCount: number;
  idNo: string | null;
}
export interface ApprovalSlaBreached {
  dealId: string;
  code: string;
  staffId: string;
  createdAt: string;
  approveDueAt: string;
  approveSlaMinutes: number;
}
export interface DepositConfirmed {
  deal: DealState;
  refs: DealRefs;
  confirmedBy: string;
}
export interface DepositRejected {
  deal: DealState;
  refs: DealRefs;
  rejectReason: string;
  rejectedBy: string;
}
export interface ContractSigned {
  deal: DealState;
  refs: DealRefs;
  contractNo: string;
  contractedAt: string;
  contractedBy: string;
}
export interface PaymentRecorded {
  deal: DealState;
  refs: DealRefs;
  paymentId: string;
  amount: number;
  method: string;
  ref: string;
  paidAt: string;
  fileCount: number;
  recordedBy: string;
}
export interface DealCompleted {
  deal: DealState;
  refs: DealRefs;
  completedAt: string;
}
export interface DeveloperFeeUpdated {
  deal: DealState;
  refs: DealRefs;
  paid: boolean;
  devPaidAt: string | null;
  updatedBy: string;
}
/** Pivot SG-5: kích hoạt mở lại căn (booking), thất bại khách (crm), thu hồi hoa hồng (commission). */
export interface DealCancelled {
  deal: DealState;
  refs: DealRefs;
  previousStatus: 'deposited' | 'contracted';
  cancelReason: string;
  refund: number;
  paidTotalAtCancel: number;
  cancelledBy: string;
}
/** Yêu cầu booking hoán đổi nguyên tử hai căn (SG-6 U1). aggregateId = dealId; correlationId = sagaId. */
export interface UnitChangeUnitSwapRequested {
  sagaId: string;
  idempotencyKey: string; // '<sagaId>:swap'
  attempt: number;
  dealId: string;
  dealVersion: number;
  fromUnitId: string;
  toUnitId: string;
  projectId: string;
  dealStaffId: string;
  customerId: string;
  /** Trạng thái căn mới = trạng thái giao dịch hiện tại. */
  targetStatus: 'pending' | 'deposited';
  requestedBy: string;
}
/** Huỷ-hoặc-xác-nhận (B-R7): chưa áp ⇒ rejected CANCELLED; đã áp ⇒ booking phát lại completed. */
export interface UnitChangeCancelRequested {
  sagaId: string;
  idempotencyKey: string; // '<sagaId>:swap'
  cancelKey: string; // '<sagaId>:swap-cancel'
  attempt: number;
  dealId: string;
  dealVersion: number;
  /**
   * Căn cũ / căn mới của yêu cầu hoán đổi (như UnitChangeUnitSwapRequested) — booking điền vào rejected{CANCELLED} khi huỷ đến
   * trước yêu cầu. Tuỳ chọn vì sự kiện phát trước khi thêm trường không có; deal-service luôn điền.
   */
  fromUnitId?: string;
  toUnitId?: string;
}
export interface UnitChanged {
  deal: DealState;
  refs: DealRefs;
  sagaId: string;
  fromUnitId: string;
  toUnitId: string;
  fromProjectId: string;
  toProjectId: string;
  fromCode: string;
  toCode: string;
  fromListPrice: number;
  toListPrice: number;
  previousValue: number;
  value: number;
  changedBy: string | null;
}
export interface UnitChangeFailed {
  sagaId: string;
  dealId: string;
  code: string;
  toUnitId: string;
  reasonCode: UnitChangeFailReason;
  message: string;
  requestedBy: string | null;
}
export interface UnitChangeStuck {
  sagaId: string;
  dealId: string;
  code: string;
  step: 'swap' | 'swap_cancel';
  attempt: number;
}
/** Thử lại đồng bộ căn (POST /deals/:id/unit-sync, đối soát đêm). */
export interface UnitSyncRequested {
  dealId: string;
  dealVersion: number;
  unitId: string;
  expectedUnitStatus: ExpectedUnitStatus;
  idempotencyKey: string; // '<dealId>:sync:<dealVersion>'
}
export interface UnitSyncStuck {
  dealId: string;
  code: string;
  unitId: string;
  unitCode: string;
  expectedUnitStatus: ExpectedUnitStatus;
  since: string;
}
export interface InstallmentDueSoon {
  dealId: string;
  code: string;
  staffId: string;
  referrerId: string | null;
  customerId: string;
  seq: number;
  label: string;
  due: string;
  remaining: number;
}
export type InstallmentOverdue = InstallmentDueSoon;

export interface EventPayloads {
  [EVENTS.submissionUnitReservationRequested]: SubmissionUnitReservationRequested;
  [EVENTS.submissionUnitReservationCancelRequested]: SubmissionUnitReservationCancelRequested;
  [EVENTS.submissionFailed]: SubmissionFailed;
  [EVENTS.submissionStuck]: SubmissionStuck;
  [EVENTS.depositSubmitted]: DepositSubmitted;
  [EVENTS.approvalSlaBreached]: ApprovalSlaBreached;
  [EVENTS.depositConfirmed]: DepositConfirmed;
  [EVENTS.depositRejected]: DepositRejected;
  [EVENTS.contractSigned]: ContractSigned;
  [EVENTS.paymentRecorded]: PaymentRecorded;
  [EVENTS.dealCompleted]: DealCompleted;
  [EVENTS.developerFeeUpdated]: DeveloperFeeUpdated;
  [EVENTS.dealCancelled]: DealCancelled;
  [EVENTS.unitChangeUnitSwapRequested]: UnitChangeUnitSwapRequested;
  [EVENTS.unitChangeCancelRequested]: UnitChangeCancelRequested;
  [EVENTS.unitChanged]: UnitChanged;
  [EVENTS.unitChangeFailed]: UnitChangeFailed;
  [EVENTS.unitChangeStuck]: UnitChangeStuck;
  [EVENTS.unitSyncRequested]: UnitSyncRequested;
  [EVENTS.unitSyncStuck]: UnitSyncStuck;
  [EVENTS.installmentDueSoon]: InstallmentDueSoon;
  [EVENTS.installmentOverdue]: InstallmentOverdue;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
